import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import PanelHelpBadge from '../../components/admin/PanelHelpBadge';
import { API_BASE_URL } from '../../config';
import DashboardModuleTabs, { DASHBOARD_WORKSPACE_HIDDEN_PATHS } from '../../components/admin/dashboard/DashboardModuleTabs';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function TinyBar({ value, max = 100, tone = 'primary' }) {
  const pct = Math.max(0, Math.min(100, (Number(value || 0) / Math.max(1, Number(max || 1))) * 100));
  const toneClass = tone === 'good' ? 'bg-emerald-500' : tone === 'warn' ? 'bg-amber-500' : tone === 'bad' ? 'bg-rose-500' : 'bg-primary';
  return (
    <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800">
      <div className={`h-2 rounded-full ${toneClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function formatPercentRate(value) {
  const n = Number(value || 0);
  return `${(n * 100).toFixed(1)}%`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString('tr-TR');
}

function actionDisplay(actionKey) {
  const map = {
    maxRecipesShown: 'Gösterilen Tarif Sayısı',
    simplifyPlan: 'Planı Sadeleştir',
    quickMealsBoost: 'Hızlı Tarif Güçlendirmesi',
    improvePrimaryReasoning: 'Birincil Öneri Gerekçe İyileştirme',
    widenSecondaryDiversity: 'Alternatif Çeşitliliği Artır',
    reassurance_blocked_cooldown: 'Destek Mesajı Sıklık Engeli'
  };
  return map[actionKey] || 'Tanımsız Aksiyon';
}

export default function BehaviorMetricsPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [days, setDays] = useState(7);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const headers = getAdminHeaders();
        const res = await fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/metrics?days=${days}`, { credentials: 'include', headers });
        const json = await res.json().catch(() => ({}));
        if (!mounted) return;
        if (!res.ok) throw new Error(json?.error || 'metrics_fetch_failed');
        setPayload(json);
      } catch (err) {
        if (mounted) setError(err?.message || 'Ölçüm verileri alınamadı');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [days]);

  const blastRows = useMemo(() => payload?.status?.blastRadius?.actions || payload?.blastRadius?.actions || [], [payload]);
  const maxActionCount = useMemo(() => Math.max(1, ...blastRows.map((x) => Number(x.action_count || 0))), [blastRows]);

  const overview = payload?.overview || {};
  const status = payload?.status || {};
  const funnel = overview?.funnel || {};

  const snapshots24h = Number(overview?.snapshots24h || status?.snapshots24h || 0);
  const anomalies24h = Number(overview?.anomalies24h || status?.anomalies24h || 0);
  const activeTemplateCount = Number(payload?.governance?.behavior_template_active_count || 0);
  const affectedUserCount = Number(overview?.actionBlastRadiusUserCount || 0);

  const shownCount = Number(funnel?.shownCount || 0);
  const acceptedCount = Number(funnel?.acceptedCount || 0);
  const completedCount = Number(funnel?.completedCount || 0);

  const acceptedRate = Number(funnel?.acceptedRate || (shownCount > 0 ? acceptedCount / shownCount : 0));
  const completedFromAcceptedRate = Number(funnel?.completedFromAcceptedRate || (acceptedCount > 0 ? completedCount / acceptedCount : 0));
  const completedFromShownRate = Number(funnel?.completedFromShownRate || (shownCount > 0 ? completedCount / shownCount : 0));

  const pipelineInconsistency = shownCount === 0 && (acceptedCount > 0 || completedCount > 0);

  const systemHealth = useMemo(() => {
    if (pipelineInconsistency || anomalies24h >= 5) {
      return {
        title: 'Kritik Sapma',
        color: 'text-rose-300 border-rose-500/50 bg-rose-500/10',
        summary: 'Huni tutarsızlığı veya yüksek anomali var. Önce veri akışı kontrol edilmeli.'
      };
    }
    if (anomalies24h > 0 || snapshots24h === 0) {
      return {
        title: 'Dikkat Gerekiyor',
        color: 'text-amber-300 border-amber-500/50 bg-amber-500/10',
        summary: 'Sistem çalışıyor ancak takip edilmesi gereken sapmalar bulunuyor.'
      };
    }
    return {
      title: 'Sistem Sağlıklı',
      color: 'text-emerald-300 border-emerald-500/50 bg-emerald-500/10',
      summary: 'Anomali görünmüyor. Karar akışı stabil ilerliyor.'
    };
  }, [pipelineInconsistency, anomalies24h, snapshots24h]);

  const decisionInterpretation = useMemo(() => {
    if (pipelineInconsistency) return 'Huni verisinde tutarsızlık var: gösterim olmadan kabul/tamamlanma görünüyor.';
    if (shownCount === 0 && acceptedCount === 0) return 'Huni verisi henüz oluşmamış. Event akışının dolması bekleniyor.';
    if (acceptedRate >= 0.6 && completedFromAcceptedRate >= 0.7) return 'Karar kalitesi güçlü: kullanıcı öneriyi kabul ediyor ve tamamlama yüksek.';
    if (acceptedRate < 0.3) return 'Kabul oranı düşük: birincil önerinin uygunluğu ve açıklaması iyileştirilmeli.';
    return 'Karar kalitesi orta seviyede: alternatif çeşitliliği ve bağlam uyumu optimize edilebilir.';
  }, [pipelineInconsistency, shownCount, acceptedCount, acceptedRate, completedFromAcceptedRate]);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0d141c]">
      <AdminSidebar
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        hiddenPaths={DASHBOARD_WORKSPACE_HIDDEN_PATHS}
      />
      <main className="flex-1 md:ml-[var(--admin-sidebar-width,18rem)] flex flex-col min-h-screen transition-[margin] duration-300">
        <AdminTopBar title="Davranış Ölçümleri" onToggleMobile={() => setMobileSidebarOpen(true)} />
        <div className="p-4 sm:p-8 flex-1 space-y-6">
          <DashboardModuleTabs />
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black text-slate-800 dark:text-white">Karar Merkezi Özeti</h1>
                <PanelHelpBadge
                  title="Karar Merkezi Özeti"
                  description="Bu ekran veri listesinden çok karar vermeyi kolaylaştıran özet ve etki katmanı sunar."
                  interpretation="Üstte sistem durumu, ortada etki/huni, altta teknik metrikler görülür."
                  metricExample="Kabul oranı artarken tamamlama sabitse sonraki adım deneyimi iyileştirilmelidir."
                />
              </div>
              <p className="text-sm text-slate-500">Anlamlı özet + karar etkisi + teknik takip</p>
              {error ? <p className="text-sm font-bold text-rose-600">{error}</p> : null}
            </div>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
              <option value={7}>7 gün</option>
              <option value={30}>30 gün</option>
              <option value={60}>60 gün</option>
            </select>
          </header>

          <section className={`rounded-2xl border p-5 ${systemHealth.color}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="text-xl font-black">{systemHealth.title}</p>
                  <PanelHelpBadge
                    title="Sistem Durumu"
                    description="Sistem sağlık seviyesi anomali, huni tutarlılığı ve veri akışıyla hesaplanır."
                    interpretation="Yeşil: sağlıklı, Sarı: dikkat, Kırmızı: kritik sapma."
                    metricExample="Anomali sayısı artarsa önce veri akışı ve event kalitesi kontrol edilir."
                  />
                </div>
                <p className="text-sm opacity-90">{systemHealth.summary}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg border border-white/20 bg-black/10 px-3 py-2">
                  <p className="text-[11px] opacity-80">Anomali (24s)</p>
                  <p className="text-lg font-black">{formatNumber(anomalies24h)}</p>
                </div>
                <div className="rounded-lg border border-white/20 bg-black/10 px-3 py-2">
                  <p className="text-[11px] opacity-80">Anlık Görüntü (24s)</p>
                  <p className="text-lg font-black">{formatNumber(snapshots24h)}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">Etkilenen Kullanıcı</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(affectedUserCount)}</p>
              <p className="mt-1 text-xs text-slate-500">Karar aksiyonundan doğrudan etkilenen benzersiz kullanıcı.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">Yönlendirilen Karar</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(acceptedCount)}</p>
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Gösterimden Kabule: {formatPercentRate(acceptedRate)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">Başarıyla Tamamlanan</p>
              <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(completedCount)}</p>
              <p className="mt-1 text-xs text-cyan-600 dark:text-cyan-400">Kabulden Tamamlanmaya: {formatPercentRate(completedFromAcceptedRate)}</p>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Birincil Karar Hunisi</h2>
              <PanelHelpBadge
                title="Birincil Karar Hunisi"
                description="Gösterim -> Kabul -> Tamamlanma akışını ve oranlarını birlikte gösterir."
                interpretation="Huni tutarsızlığı varsa önce event pipeline ve mapping kontrol edilir."
                metricExample="Gösterim yüksek ama kabul düşükse birincil önerinin ilgisi zayıf olabilir."
              />
            </div>
            <p className={`mb-4 text-sm ${pipelineInconsistency ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'}`}>{decisionInterpretation}</p>
            <div className="space-y-3">
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Gösterim</span>
                  <span className="text-slate-500">{shownCount}</span>
                </div>
                <TinyBar value={shownCount} max={Math.max(1, shownCount)} tone="primary" />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Kabul</span>
                  <span className="text-slate-500">{acceptedCount} ({formatPercentRate(acceptedRate)})</span>
                </div>
                <TinyBar value={acceptedCount} max={Math.max(1, shownCount)} tone={acceptedRate < 0.3 ? 'bad' : 'good'} />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Tamamlanan</span>
                  <span className="text-slate-500">{completedCount} ({formatPercentRate(completedFromShownRate)})</span>
                </div>
                <TinyBar value={completedCount} max={Math.max(1, shownCount)} tone={completedFromAcceptedRate < 0.5 ? 'warn' : 'good'} />
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Bağlam Analizi</h2>
                <PanelHelpBadge
                  title="Bağlam Analizi"
                  description="Son anlık görüntü segmentini ve uyum değerini gösterir."
                  interpretation="Segment kayması veya uyum düşüşü davranış değişimini erken yakalamayı sağlar."
                  metricExample="Plan uyumu ardışık düşüyorsa segment geçişi kuralı tetiklenebilir."
                />
              </div>
              {loading ? <p className="text-sm text-slate-500">Yükleniyor...</p> : null}
              <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <p>Son Segment: <span className="font-bold">{status?.lastSnapshot?.decision_segment || '-'}</span></p>
                <p>Son Uyum: <span className="font-bold">{Number(status?.lastSnapshot?.plan_adherence || 0).toFixed(2)}</span></p>
                <p>Son Anlık Görüntü Zamanı: <span className="font-bold">{String(status?.lastSnapshot?.created_at || '-').replace('T', ' ').slice(0, 19)}</span></p>
                <p>Bağlam Yakalama Oranı: <span className="font-bold">{formatPercentRate(overview?.contextCaptureRate || 0)}</span></p>
                <p>Sonraki Hafta İyileşme: <span className="font-bold">{formatPercentRate(overview?.contextToNextWeekImprovementRate || 0)}</span></p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Etki Alanı</h2>
                <PanelHelpBadge
                  title="Etki Alanı"
                  description="Hangi aksiyon anahtarının kaç olay ve kaç kullanıcıya dokunduğunu gösterir."
                  interpretation="Yüksek aksiyon yoğunluğu olan alanlarda kural etkisi daha belirgindir."
                  metricExample="Aksiyon olayı artıp kullanıcı sayısı sabitse aynı kullanıcıda tekrarlı müdahale vardır."
                />
              </div>
              <div className="space-y-3">
                {blastRows.length === 0 ? (
                  <p className="text-sm text-slate-500">Bu pencerede etki verisi yok.</p>
                ) : blastRows.map((row, idx) => (
                  <div key={`${row.action_key}-${idx}`} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{actionDisplay(row.action_key)}</span>
                      <span className="text-slate-500">Olay: {row.action_count} · Kullanıcı: {row.user_count}</span>
                    </div>
                    <p className="text-[11px] text-slate-500">Teknik anahtar: {row.action_key}</p>
                    <TinyBar value={row.action_count} max={maxActionCount} tone="warn" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
            <div className="mb-3 flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Teknik Gösterge Katmanı</h2>
              <PanelHelpBadge
                title="Teknik Gösterge Katmanı"
                description="Bu bölüm operasyonel detay ve yönetim metriklerini verir."
                interpretation="Yorumdan çok doğrulama amaçlıdır; ana kararlar üst panellerden alınmalıdır."
                metricExample="Anlık görüntü düşükse metrik yorumu önce veri toplama sağlığı ile birlikte okunur."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase text-slate-500">Anlık Görüntü (24s)</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(snapshots24h)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase text-slate-500">Anomali (24s)</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(anomalies24h)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase text-slate-500">Aktif Şablon</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(activeTemplateCount)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase text-slate-500">Ort. Karar Süresi</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{formatNumber(overview?.avgDecisionTimeMs)} ms</p>
                <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Azalış: {formatNumber(overview?.decisionTimeReductionMs)} ms</p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
